import UserContext from "Context/UserProvider";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import ChargingPartnerTable from "../../components/charging/ChargingPartnerComponent/ChargingPartnerTable";
import { ButtonGroup, Button, Box, CircularProgress } from '@mui/material';
import { useApiService } from "../../services/ApiService/ApiService";

function ChargingPartnerListPage(props: any) {
    const [showCategory, setShowCategory] = useState([true, false, false]) as any;
    const [activeFilter, setActiveFilter] = useState("ALL");
    const {userInfo, setUserInfo} = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [chargingPartners, setChargingPartners] = useState([]) as any;
    const [filteredPartners, setFilteredPartners] = useState([]) as any;
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchChargingPartners = async () => {
        try {
            setLoading(true);
            let bodyData: { [name: string]: string } = {};

            bodyData["uuid"] = userInfo.uuid;
            bodyData["businessUuid"] = userInfo.businessUuid;

            const response = await fetchWithAuth('/charging-partner/list', {
                method: 'POST',
                body: JSON.stringify(bodyData),
            });

            if (!response.ok) {
                setIsError(true);
                return;
            }

            const res_json = await response.json();

            const mappedPartners = res_json.chargingPartners.map((e: any) => ({ 
                ...e, 
                id: (e.consumerChargingPartnerUuid === null ? e.supplierChargingPartnerUuid : e.consumerChargingPartnerUuid), 
                chargingPartnerBusinessName: e.name, 
                workflowState: e.supplierWorkflowState, 
                visitorWorkflowState: e.consumerWorkflowState, 
                partnerUuid: null, 
                rateCard: null, 
                providerBusinessUuid: e.businessUuid, 
                consumerBusinessUuid: e.partnerBusinessUuid, 
                businessCategory: e.businessCategory, 
                canOfferCharging: e.canOfferCharging, 
                canRequestCharging: e.canRequestCharging, 
                chargingPartnerBusinessCategory: e.partnerCategory 
            }));

            console.log('Fetched partners:', mappedPartners);
            setChargingPartners(mappedPartners);
            setFilteredPartners(mappedPartners);
        } catch (error) {
            console.error('Error fetching partners:', error);
            setIsError(true);
        } finally {
            setLoading(false);
        }
    };

    const requestPartnership = async (chargingPartnerUuid: string, chargingPartnerBusinessName: string, rowId: any, workflowState: string, businessCategory: string, canOfferCharging: boolean, canRequestCharging: boolean, partnershipRequestType: string, providerBusinessUuid: string, consumerBusinessUuid: string) => {
        console.log(providerBusinessUuid, consumerBusinessUuid);
        navigate("/share/business/charging-partners/request", {
            state: {
                chargingPartnerUuid: chargingPartnerUuid,
                providerBusinessUuid: providerBusinessUuid,
                consumerBusinessUuid: consumerBusinessUuid,
                chargingPartnerBusinessName: chargingPartnerBusinessName,
                businessCategory: businessCategory,
                canOfferCharging: canOfferCharging,
                canRequestCharging: canRequestCharging,
                workflowState: workflowState,
                partnershipRequestType: partnershipRequestType,
            },
        })
    };

    useEffect(() => {
        fetchChargingPartners();
    }, []);

    // Filter partners whenever activeFilter changes
    useEffect(() => {
        if (!chargingPartners || chargingPartners.length === 0) return;

        console.log('Filtering with:', activeFilter);
        console.log('Current partners:', chargingPartners);

        if (activeFilter === "ALL") {
            console.log('Setting all partners:', chargingPartners);
            setFilteredPartners(chargingPartners);
            return;
        }

        const filtered = chargingPartners.filter((partner: any) => {
            const providerState = partner.workflowState;
            const consumerState = partner.visitorWorkflowState;

            console.log(`Partner ${partner.chargingPartnerBusinessName}:`, { providerState, consumerState });

            // Special handling for OFFERED state
            if (activeFilter === "OFFERED") {
                return providerState === "OFFERED" || consumerState === "OFFERED";
            }

            // Special handling for REQUESTED state
            if (activeFilter === "REQUESTED") {
                return providerState === "REQUESTED" || consumerState === "REQUESTED";
            }

            // For other states, check both states
            return providerState === activeFilter || consumerState === activeFilter;
        });

        console.log('Filtered partners:', filtered);
        setFilteredPartners(filtered);
    }, [activeFilter, chargingPartners]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <ButtonGroup variant="outlined" size="large">
                    <Button 
                        onClick={() => setActiveFilter("ALL")}
                        variant={activeFilter === "ALL" ? "contained" : "outlined"}
                    >
                        All ({chargingPartners.length})
                    </Button>
                    <Button 
                        onClick={() => setActiveFilter("AGREED")}
                        variant={activeFilter === "AGREED" ? "contained" : "outlined"}
                    >
                        Agreed
                    </Button>
                    <Button 
                        onClick={() => setActiveFilter("OFFERED")}
                        variant={activeFilter === "OFFERED" ? "contained" : "outlined"}
                    >
                        Offered
                    </Button>
                    <Button 
                        onClick={() => setActiveFilter("REQUESTED")}
                        variant={activeFilter === "REQUESTED" ? "contained" : "outlined"}
                    >
                        Requested
                    </Button>
                </ButtonGroup>
            </Box>

            <ChargingPartnerTable 
                chargingPartners={filteredPartners} 
                requestPartnership={requestPartnership}
            />
        </Box>
    );
}

export default ChargingPartnerListPage;